import { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import ErrorBoundary from "components/ErrorBoundary";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useSession } from "hooks/useSession";

/**
 * Route layout for the new experience page.
 *
 * A focused alternative to the app-wide `Element` wrapper. It composes
 * only the chrome the experience page needs:
 *   - Auth gate via `useSession` (the app's own session hook)
 *   - Intercom (launcher hidden — the page is immersive)
 *   - `ErrorBoundary`
 *
 * Deliberately omits everything else, so creator mode is as immersive as
 * the viewer-facing experience in the sales app:
 *   - NO admin left sidebar (MED-6950) — the app-wide menu is cognitive
 *     overload next to the experience's own header + Knowledge Hub, so the
 *     page owns the full viewport (mirrors sales, which has no chrome).
 *   - NO styled-components `<ThemeProvider theme={adminTheme}>` — the
 *     page scopes its own `<ConfigProvider theme={libertifyThemeV2}>`.
 *   - NO `<BackgroundImage>` — the experience page owns its full canvas.
 */
export const ExperienceV2Layout = () => {
  const { user, isAuthenticated, isLoading } = useSession();

  useEffect(() => {
    if (isLoading) {
      return; // avoid init intercom too early
    }
    let userProps = {};
    if (user && isAuthenticated) {
      const { id, first_name, last_name, email, created } = user;
      userProps = {
        user_id: String(id),
        name: `${first_name} ${last_name}`,
        email,
        created_at: created ? new Date(created).getTime() / 1000 : 0,
      };
    }
    Intercom({
      app_id: "mcwfcpeh",
      ...userProps,
      // Immersive page — keep the launcher hidden.
      hide_default_launcher: true,
    });

    return () => {
      shutdown();
    };
  }, [user, isAuthenticated, isLoading]);

  // Gate render until session loads, so the page never mounts (and fetches)
  // against a half-resolved session (matches Element).
  if (isLoading) {
    return <LoadingSpinner tip="" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <ErrorBoundary id="experience-v2">
      <div
        style={{
          height: "100dvh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Outlet />
      </div>
    </ErrorBoundary>
  );
};
